<template>
  <b-modal
    id="actions-confirm-modal"
    ref="actions-confirm-modal"
    size="lg"
    hide-header-close
    no-close-on-esc
    no-close-on-backdrop
    centered
  >
    <template v-slot:modal-header>
      <div class="d-flex align-items-center">
        <h5 class="modal-title mb-0">
          <span
            v-if="action === 'delete'"
            class="icon mr-2"
          >
            <i class="fas fa-exclamation-triangle warning" />
          </span>
          {{ getModalHeader }}
        </h5>
      </div>
    </template>
    <section>
      <div
        v-if="!areAllArticlesSelected"
      >
        <p class="mb-0">
          <template v-if="action === 'change-language'">
            The following articles will have their language changed to <b>{{ newSelectedLanguage }}</b>.
          </template>
          <template v-else>
            Are you sure you want to {{ action }} the following <b>{{ selectedItemsAmountText }}</b> articles?
          </template>
        </p>
        <br>
        <ul
          class="pl-4 mb-0"
        >
          <li
            v-for="(id) in selectedIds"
            :key="id"
          >
            <p
              class="mb-0 ellipsis"
            >
              {{ getArticleTitle(id) }}
            </p>
          </li>
        </ul>
      </div>
      <div v-else>
        <p class="mb-0">
          <template v-if="action === 'change-language'">
            Are you sure you want to change the language for selected all articles in this campaign to <b>{{ newSelectedLanguage }}</b>.
          </template>
          <template v-else>
            Are you sure you want to {{ action }} all selected articles in this campaign?
          </template>
        </p>
      </div>
    </section>
    <template v-slot:modal-footer>
      <b-button
        variant="tertiary"
        @click="closeModal()"
        data-test="cancel-bulk-action-btn"
      >
        Cancel
      </b-button>
      <b-button
        :variant="`${action === 'delete' ? 'danger' : 'primary'}`"
        :class="{ 'btn-loading': isConfirmButtonLoading }"
        @click="confirmAction(action)"
        data-test="confirm-bulk-action-btn"
      >
        <template v-if="action === 'change-language'">
          Submit
        </template>
        <template v-else>
          {{ capitalizeFirstLetter(action) }}
        </template>
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as accountTypes from '@/modules/account/store/types';

import textHelper from '@/core/mixins/textHelper';

import ArticlesActionsMixins from '@/modules/content/components/ContentPage/mixins/ArticlesActionsMixins';


export default {
  name: 'ActionsConfirmationModal',
  mixins: [
    textHelper,
    ArticlesActionsMixins,
  ],
  props: {
    customProps: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      isConfirmButtonLoading: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'scrapingBatchId',
      'languages',
      'feedId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ARTICLE_BY_ID,
    ]),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    action() {
      if (!this.customProps.hasOwnProperty('action')) return '';
      return this.customProps.action;
    },
    selectedIds() {
      if (!this.customProps.hasOwnProperty('ids')) return [];
      return this.customProps.ids;
    },
    areAllArticlesSelected() {
      if (!this.customProps.hasOwnProperty('areAllArticlesSelected')) return false;
      return this.customProps.areAllArticlesSelected;
    },
    newLanguageId() {
      if (!this.customProps.hasOwnProperty('newLanguageId')) return null;
      return this.customProps.newLanguageId;
    },
    selectedItemsAmountText() {
      return this.hasSelectedIds ? this.selectedIds.length : '';
    },
    hasSelectedIds() {
      return this.selectedIds.length > 0;
    },
    getModalHeader() {
      if (!this.action) return 'Confirmation modal';
      if (this.action === 'change-language') return 'Change Articles Language';
      return `${this.capitalizeFirstLetter(this.action)} Articles`;
    },
    newSelectedLanguage() {
      if (!this.newLanguageId) return 'No Language Selected';
      return this.languages.find((language) => language.id === this.newLanguageId).name;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_CONTENT_ITEM_PUBLISHING_STATE,
      contentTypes.SET_CONTENT_ITEMS_URLS_CHANGED,
      contentTypes.PATCH_LANGUAGES,
      contentTypes.DELETE_ARTICLES,
    ]),
    async confirmAction(action) {
      try {
        this.isConfirmButtonLoading = true;
        switch (action) {
          case 'publish': {
            await this.confirmPublish();
            break;
          }
          case 'pause': {
            await this.confirmPause();
            break;
          }
          case 'change-language': {
            await this.confirmChangeLanguage();
            break;
          }
          case 'delete': {
            await this.confirmDelete();
            break;
          }
          default: return Promise.reject();
        }
        this.$emit('confirmAction');
        this.$emit('resetCustomProps');
      } finally {
        this.closeModal();
        this.isConfirmButtonLoading = false;
      }
    },
    async confirmPublish() {
      if (!this.canPublishArticles({
        ids: this.selectedIds,
      })) return;
      const payload = {
        advertiserId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        contentItemIds: this.selectedIds,
        action: 'PUBLISH',
      };
      if (this.areAllArticlesSelected) delete payload.contentItemIds;
      return this.SET_CONTENT_ITEM_PUBLISHING_STATE(payload);
    },
    async confirmPause() {
      if (!this.canPauseArticles({
        ids: this.selectedIds,
      })) return;
      const payload = {
        advertiserId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        contentItemIds: this.selectedIds,
        action: 'UNPUBLISH',
      };
      if (this.areAllArticlesSelected) delete payload.contentItemIds;
      return this.SET_CONTENT_ITEM_PUBLISHING_STATE(payload);
    },
    async confirmChangeLanguage() {
      if (!this.canChangeArticlesLanguage) return;
      const payload = {
        advertiserId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        contentItemIds: this.selectedIds,
        languageId: this.newLanguageId,
      };
      if (this.areAllArticlesSelected) delete payload.contentItemIds;
      return this.PATCH_LANGUAGES(payload);
    },
    async confirmDelete() {
      if (!this.canDeleteArticles({
        ids: this.selectedIds,
      })) return;
      const payload = {
        advertiserId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        campaignId: this.campaign.id,
        contentScrapeId: this.scrapingBatchId,
        ids: this.selectedIds,
      };
      if (this.areAllArticlesSelected) delete payload.ids;
      return this.DELETE_ARTICLES(payload).then(() => {
        this.SET_CONTENT_ITEMS_URLS_CHANGED(true);
      });
    },
    getArticleTitle(id) {
      const article = this.GET_ARTICLE_BY_ID(id);
      return article?.title || article?.url;
    },
    closeModal() {
      this.$root.$emit('bv::hide::modal', 'actions-confirm-modal');
    },
  },
};
</script>
